// Zvuková vrstva Kolonie: seznamy (rychlé rozhodování), procházení mapy po rozích (orientace),
// hlášení s prioritami (1 = pro tebe hned, 2 = týká se tě, 3 = jen do historie a do shrnutí tahu).
import type { GameEvent } from "../../../games/types.ts";
import { COSTS, RESOURCES, type Bag, type Dev, type KolonieAction, type KolonieView, type Res, type TradeOffer } from "../../../games/kolonie/meta.ts";
import { distanceOk, settlementOk } from "../../../games/kolonie/logic.ts";
import { TOPOLOGY as T, edgeBetween, otherEnd } from "../../../games/kolonie/topology.ts";
import { announce, say } from "../../announce.ts";
import { play, queue } from "../../audio.ts";
import {
  afford,
  cornerContent,
  cornerNeighbors,
  cornerPort,
  cornerStrength,
  cornerSummary,
  edgeOwner,
  edgeSummary,
  missing,
  myPending,
  name,
  rateText,
  setupCorner,
  targets,
  whatNow,
  type Model,
} from "./model.ts";
import { bagText, bagTotal, cards, DEV_HELP, DEV_NAME, hexText, pointsText, resCount, resName } from "./texts.ts";
import "./sounds.ts";

export const HELP_TEXT =
  "Nápověda. Šipky vlevo a vpravo: po řadě rohů. Nahoru a dolů: po svislé hraně do vedlejší řady. Home a End: kraj řady. Page Up a Page Down: o řadu výš a níž. " +
  "Shift a šipka: vybere hranu tím směrem, Enter na ní postaví tunel. Enter na rohu: postaví základnu nebo město. Mezerník: podrobnosti o rohu. " +
  "S: stavět, seznam míst seřazených podle síly. K: hodit kostkami. U: ukončit tah. V: karty výzkumu. N: banka a stanice. O: obchod s hráči. " +
  "Na nabídku od jiného hráče: A přijmout, D odmítnout, P protinabídka. " +
  "R: stav hry. Z: tvoje zásoby. B: body všech. C: ceník. L: historie hlášení. M: skok na tvoje stavby. X: skok k pirátům. H: nápověda, Shift a H: co teď smím. " +
  "Nastavení: Shift a P přepíná, jak se hlásí čísla, Shift a C zapíná a vypíná potvrzování staveb. " +
  "V seznamu: šipky nahoru a dolů, vlevo a vpravo mění počet, Enter vybere, G skočí na místo na mapě, Escape zavře.";

interface Item {
  text: string;
  action?: () => void;
  adjust?: (delta: number) => void;
  /** Roh na mapě (klávesa G). */
  jump?: number;
}

interface List {
  title: string;
  items: () => Item[];
  index: number;
  onClose?: () => void;
}

export class SoundLayer {
  private model: Model;
  private board: HTMLElement;
  private onChange: () => void;
  private list: List | null = null;
  private confirm: { text: string; run: () => void } | null = null;
  private onKey = (e: KeyboardEvent) => this.handleKey(e);
  /** Rozpracovaná nabídka a výběry (protinabídka, zásoby). */
  private draft: Record<Res, number> = { kov: 0, sklo: 0, voda: 0, jidlo: 0, energie: 0 };
  private discardDraft: Bag = {};
  private yop: Res[] = [];
  private autoOpened = "";

  constructor(model: Model, board: HTMLElement, onChange: () => void) {
    this.model = model;
    this.board = board;
    this.onChange = onChange;
    board.addEventListener("keydown", this.onKey);
  }

  destroy() {
    this.board.removeEventListener("keydown", this.onKey);
  }

  private get v(): KolonieView {
    return this.model.view!;
  }

  private send(a: KolonieAction) {
    this.model.send(a);
  }

  /** Hlášení s prioritou: 1 = přeruší, 2 = běžné, 3 = jen historie a shrnutí. */
  private tell(text: string, priority: 1 | 2 | 3) {
    if (!text) return;
    this.model.log.unshift(text);
    if (this.model.log.length > 60) this.model.log.length = 60;
    if (priority === 1) say(text);
    else if (priority === 2) announce(text);
    else this.model.since.push(text);
  }

  // ---------- Klávesy ----------

  private handleKey(e: KeyboardEvent) {
    if (!this.model.view || e.ctrlKey || e.altKey || e.metaKey) return;
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    let handled = true;

    if (this.confirm) {
      if (key === "Enter" || key === " ") {
        const c = this.confirm;
        this.confirm = null;
        c.run();
      } else if (key === "Escape") {
        this.confirm = null;
        say("Zrušeno.");
      } else handled = false;
      if (handled) return void e.preventDefault();
    }

    if (this.list && this.listKey(key, e.shiftKey)) return void e.preventDefault();

    if (e.shiftKey && key === "p") this.cycleProb();
    else if (e.shiftKey && key === "c") this.toggleConfirm();
    else if (e.shiftKey && key.startsWith("Arrow")) this.selectEdge(key);
    else
      switch (key) {
        case "ArrowLeft":
          this.move(this.rowNeighbor(-1), "vlevo");
          break;
        case "ArrowRight":
          this.move(this.rowNeighbor(1), "vpravo");
          break;
        case "ArrowUp":
        case "ArrowDown":
          this.move(this.verticalNeighbor(key === "ArrowUp" ? -1 : 1), key === "ArrowUp" ? "nahoru" : "dolů");
          break;
        case "Home":
        case "End": {
          const row = T.corners.map((_, i) => i).filter((i) => T.corners[i].row === T.corners[this.model.cursor].row);
          this.move(key === "Home" ? row[0] : row[row.length - 1], "");
          break;
        }
        case "PageUp":
        case "PageDown":
          this.move(this.nearestInRow(T.corners[this.model.cursor].row + (key === "PageUp" ? -1 : 1)), "");
          break;
        case "Enter":
          this.enterOnMap();
          break;
        case " ":
          say(this.cornerDetail(this.model.cursor));
          break;
        case "Escape":
          if (this.model.edge !== null) {
            this.model.edge = null;
            this.onChange();
            say("Výběr hrany zrušen.");
          } else handled = false;
          break;
        case "s":
          this.openBuildMenu();
          break;
        case "k":
          this.roll();
          break;
        case "u":
          this.endTurn();
          break;
        case "v":
          this.openCards();
          break;
        case "n":
          this.openBank();
          break;
        case "o":
          this.openTrade();
          break;
        case "a":
          this.respond("accept");
          break;
        case "d":
          this.respond("decline");
          break;
        case "p":
          this.openCounter();
          break;
        case "r":
          say(this.statusText());
          break;
        case "z":
          say(this.handText());
          break;
        case "b":
          say(this.scoreText());
          break;
        case "c":
          say(this.costsText());
          break;
        case "l":
          this.openHistory();
          break;
        case "m":
          this.jumpMine(e.shiftKey ? -1 : 1);
          break;
        case "x":
          this.jumpRobber();
          break;
        case "h":
          say(e.shiftKey ? whatNow(this.v) : HELP_TEXT);
          break;
        default:
          handled = false;
      }
    if (handled) e.preventDefault();
  }

  private cycleProb() {
    const order = ["cislo", "slovo", "tecky"] as const;
    this.model.prob = order[(order.indexOf(this.model.prob) + 1) % order.length];
    try {
      localStorage.setItem("ha.kolonie.prob", this.model.prob);
    } catch {
      /* nic */
    }
    say({ cislo: "Čísla jen číslem.", slovo: "Čísla i slovem, jak často padají.", tecky: "Čísla i s počtem teček." }[this.model.prob]);
  }

  private toggleConfirm() {
    this.model.confirmBuild = !this.model.confirmBuild;
    try {
      localStorage.setItem("ha.kolonie.confirm", this.model.confirmBuild ? "1" : "0");
    } catch {
      /* nic */
    }
    say(this.model.confirmBuild ? "Potvrzování staveb zapnuto." : "Potvrzování staveb vypnuto, stavím hned.");
  }

  // ---------- Mapa ----------

  private rowNeighbor(dir: -1 | 1): number | null {
    const c = T.corners[this.model.cursor];
    const i = T.corners.findIndex((o) => o.row === c.row && o.col === c.col + dir);
    return i >= 0 ? i : null;
  }

  private verticalNeighbor(dir: -1 | 1): number | null {
    const c = this.model.cursor;
    for (const e of T.corners[c].edges) {
      if (!T.edges[e].vertical) continue;
      const o = otherEnd(e, c);
      if (Math.sign(T.corners[o].y - T.corners[c].y) === dir) return o;
    }
    return null;
  }

  private nearestInRow(row: number): number | null {
    const x = T.corners[this.model.cursor].x;
    const inRow = T.corners.map((_, i) => i).filter((i) => T.corners[i].row === row);
    if (!inRow.length) return null;
    return inRow.sort((a, b) => Math.abs(T.corners[a].x - x) - Math.abs(T.corners[b].x - x))[0];
  }

  private panFor(corner: number): number {
    return Math.max(-1, Math.min(1, T.corners[corner].x / 4.4));
  }

  private move(to: number | null, dir: string) {
    if (to === null) {
      play("okraj", { pan: this.panFor(this.model.cursor) });
      if (dir) say(`${dir} nevede hrana`);
      return;
    }
    const from = this.model.cursor;
    const edge = edgeBetween(from, to);
    this.model.cursor = to;
    this.model.edge = null;
    this.onChange();
    const v = this.v;
    const buildable = distanceOk(v, to) && (myPending(v).some((p) => p.kind === "setupSettlement") || settlementOk(v, v.me!, to));
    play(buildable ? "kolonie-misto" : "kurzor", { pan: this.panFor(to) });
    const owner = edge >= 0 ? edgeOwner(v, edge) : null;
    const prefix = owner ? `po hraně, ${owner}; ` : "";
    say(`${prefix}${this.cornerShort(to)}`);
  }

  /** Krátké hlášení rohu při pohybu: obsah, sousedé, stanice. */
  private cornerShort(c: number): string {
    const v = this.v;
    const port = cornerPort(v, c);
    return `${cornerContent(v, c)}; ${cornerNeighbors(v, c, this.model.prob)}${port ? `; ${port}` : ""}`;
  }

  private cornerDetail(c: number): string {
    const v = this.v;
    const edges = T.corners[c].edges.map((e) => {
      const dir = T.edges[e].vertical ? (T.corners[otherEnd(e, c)].y < T.corners[c].y ? "nahoru" : "dolů") : T.corners[otherEnd(e, c)].x < T.corners[c].x ? "vlevo" : "vpravo";
      return `${dir}: ${edgeOwner(v, e) ?? "volná hrana"}`;
    });
    const where = `řada ${T.corners[c].row + 1}, roh ${T.corners[c].col + 1}`;
    return `${cornerContent(v, c)}. Sousedí s ${cornerNeighbors(v, c, this.model.prob)}. Síla ${cornerStrength(v, c)}.${cornerPort(v, c) ? ` ${cornerPort(v, c)}.` : ""} Hrany: ${edges.join(", ")}. ${where}.`;
  }

  private selectEdge(key: string) {
    const c = this.model.cursor;
    let to: number | null = null;
    if (key === "ArrowLeft") to = this.rowNeighbor(-1);
    else if (key === "ArrowRight") to = this.rowNeighbor(1);
    else to = this.verticalNeighbor(key === "ArrowUp" ? -1 : 1);
    if (to === null) {
      play("okraj");
      return say("Tím směrem nevede hrana.");
    }
    const e = edgeBetween(c, to);
    this.model.edge = e;
    this.onChange();
    const v = this.v;
    const owner = edgeOwner(v, e);
    const can = this.roadKind() !== null && this.legalRoad(e);
    play(can ? "kolonie-misto" : "kurzor", { pan: this.panFor(c) });
    say(`Hrana: ${owner ?? "volná"}${can ? ", tunel tu můžeš postavit, Enter" : ""}.`);
  }

  /** Jaký tunel teď smím stavět (rozestavění, zdarma, nebo za suroviny). */
  private roadKind(): "setupRoad" | "freeRoad" | "road" | null {
    const kinds = myPending(this.v).map((p) => p.kind);
    if (kinds.includes("setupRoad")) return "setupRoad";
    if (kinds.includes("roadBuilding")) return "freeRoad";
    if (kinds.includes("main")) return "road";
    return null;
  }

  private legalRoad(e: number): boolean {
    const kind = this.roadKind();
    if (!kind) return false;
    return targets(this.v, kind).includes(e);
  }

  private enterOnMap() {
    const v = this.v;
    const kinds = myPending(v).map((p) => p.kind);
    if (this.model.edge !== null) {
      const e = this.model.edge;
      const kind = this.roadKind();
      if (!kind || !this.legalRoad(e)) {
        play("okraj");
        return say(kind ? "Tady tunel stavět nemůžeš: nenavazuje na tvou stavbu nebo tunel, nebo je obsazený." : "Teď tunel stavět nemůžeš.");
      }
      if (kind === "road" && !afford(v, COSTS.tunel)) return say(`Na tunel ti chybí: ${bagText(missing(v, COSTS.tunel))}.`);
      return this.buildRoad(e, kind);
    }
    const c = this.model.cursor;
    if (kinds.includes("setupSettlement")) {
      if (!distanceOk(v, c)) return say("Tady stavět nemůžeš: příliš blízko jiné stavby nebo obsazeno.");
      return this.withConfirm(`Postavit základnu: ${cornerSummary(v, c, this.model.prob)}?`, () => this.send({ type: "placeSettlement", corner: c }));
    }
    if (!kinds.includes("main")) return say(whatNow(v));
    const b = v.corners[c];
    if (b && b.owner === v.me && !b.city) {
      if (!afford(v, COSTS.mesto)) return say(`Na město ti chybí: ${bagText(missing(v, COSTS.mesto))}.`);
      return this.withConfirm("Postavit město místo základny?", () => this.send({ type: "buildCity", corner: c }));
    }
    if (b) return say("Tady už stojí stavba.");
    if (!distanceOk(v, c)) return say("Tady stavět nemůžeš: příliš blízko jiné stavby.");
    if (!settlementOk(v, v.me!, c)) return say("Tady stavět nemůžeš: nevede sem tvůj tunel.");
    if (!afford(v, COSTS.zakladna)) return say(`Na základnu ti chybí: ${bagText(missing(v, COSTS.zakladna))}.`);
    this.withConfirm(`Postavit základnu: ${cornerSummary(v, c, this.model.prob)}?`, () => this.send({ type: "buildSettlement", corner: c }));
  }

  private buildRoad(e: number, kind: "setupRoad" | "freeRoad" | "road") {
    const text = `Postavit tunel ${edgeSummary(this.v, e, this.model.prob)}?`;
    const run = () => this.send(kind === "setupRoad" ? { type: "placeRoad", edge: e } : { type: "buildRoad", edge: e });
    this.model.edge = null;
    this.withConfirm(text, run);
  }

  private withConfirm(text: string, run: () => void) {
    if (!this.model.confirmBuild) return run();
    this.confirm = { text, run };
    say(`${text} Enter ano, Escape ne.`);
  }

  private jumpMine(dir: 1 | -1) {
    const v = this.v;
    const mine = v.corners.map((b, c) => (b?.owner === v.me ? c : -1)).filter((c) => c >= 0);
    if (!mine.length) return say("Zatím nemáš žádnou stavbu.");
    const i = mine.indexOf(this.model.cursor);
    const next = mine[(i + dir + mine.length) % mine.length];
    this.model.cursor = next;
    this.onChange();
    play("kurzor", { pan: this.panFor(next) });
    say(this.cornerShort(next));
  }

  private jumpRobber() {
    const v = this.v;
    const hex = T.hexes[v.robber];
    this.model.cursor = hex.corners[0];
    this.onChange();
    say(`Piráti jsou na poli ${hexText(v.hexes[v.robber], this.model.prob)}, ${hex.label}. Kurzor na horním rohu toho pole.`);
  }

  // ---------- Seznamy ----------

  private open(list: List) {
    this.list = list;
    const items = list.items();
    if (!items.length) {
      this.list = null;
      return say(`${list.title}: nic.`);
    }
    list.index = Math.min(list.index, items.length - 1);
    say(`${list.title}. ${items[list.index].text}`);
  }

  private close(silent = false) {
    const l = this.list;
    this.list = null;
    l?.onClose?.();
    if (!silent) say("Zavřeno.");
  }

  private listKey(key: string, shift: boolean): boolean {
    const l = this.list!;
    const items = l.items();
    if (!items.length) {
      this.list = null;
      return false;
    }
    l.index = Math.min(l.index, items.length - 1);
    const item = items[l.index];
    switch (key) {
      case "ArrowDown":
      case "ArrowUp": {
        const n = l.index + (key === "ArrowDown" ? 1 : -1);
        if (n < 0 || n >= items.length) play("okraj");
        else {
          l.index = n;
          play("kurzor");
        }
        say(items[l.index].text);
        return true;
      }
      case "Home":
      case "End":
        l.index = key === "Home" ? 0 : items.length - 1;
        say(items[l.index].text);
        return true;
      case "ArrowLeft":
      case "ArrowRight":
        if (shift) return false;
        if (item.adjust) {
          item.adjust(key === "ArrowRight" ? 1 : -1);
          say(l.items()[l.index].text);
        } else play("okraj");
        return true;
      case "Enter":
      case " ":
        if (item.action) item.action();
        else if (item.adjust) {
          item.adjust(1);
          say(l.items()[l.index].text);
        } else play("okraj");
        return true;
      case "g":
        if (item.jump !== undefined) {
          this.model.cursor = item.jump;
          this.model.edge = null;
          this.onChange();
          this.close(true);
          say(`Na mapě: ${this.cornerShort(item.jump)}`);
        } else play("okraj");
        return true;
      case "Escape":
        this.close();
        return true;
    }
    return false;
  }

  private openBuildMenu() {
    const v = this.v;
    const kinds = myPending(v).map((p) => p.kind);
    if (kinds.includes("setupSettlement")) return this.openSettlementList("setupSettlement");
    if (kinds.includes("setupRoad") || kinds.includes("roadBuilding")) return this.openRoadList(kinds.includes("setupRoad") ? "setupRoad" : "freeRoad");
    if (!kinds.includes("main")) return say(whatNow(v));
    const status = (cost: Bag, places: number, left: number) =>
      left <= 0 ? "už nemáš žádné" : !places ? "není kde stavět" : afford(v, cost) ? `${places} ${places === 1 ? "místo" : places <= 4 ? "místa" : "míst"}` : `chybí ${bagText(missing(v, cost))}`;
    const pieces = v.players[v.me!].pieces;
    this.open({
      title: "Stavět",
      index: 0,
      items: () => [
        { text: `Město: ${status(COSTS.mesto, targets(v, "city").length, pieces.mesto)}`, action: () => this.openSettlementList("city") },
        { text: `Základna: ${status(COSTS.zakladna, targets(v, "settlement").length, pieces.zakladna)}`, action: () => this.openSettlementList("settlement") },
        { text: `Tunel: ${status(COSTS.tunel, targets(v, "road").length, pieces.tunel)}`, action: () => this.openRoadList("road") },
        {
          text: `Karta výzkumu: ${v.devDeck === 0 ? "balíček je prázdný" : afford(v, COSTS.vyzkum) ? "můžeš koupit" : `chybí ${bagText(missing(v, COSTS.vyzkum))}`}`,
          action: () => {
            if (!afford(v, COSTS.vyzkum) || !v.devDeck) return say("Kartu výzkumu teď koupit nemůžeš.");
            this.close(true);
            this.send({ type: "buyDev" });
          },
        },
      ],
    });
  }

  private openSettlementList(kind: "setupSettlement" | "settlement" | "city") {
    const v = this.v;
    const cost = kind === "city" ? COSTS.mesto : COSTS.zakladna;
    if (kind !== "setupSettlement" && !afford(v, cost)) return say(`Chybí ti: ${bagText(missing(v, cost))}.`);
    const title = kind === "city" ? "Kde postavit město" : "Místa pro základnu, nejsilnější první";
    this.open({
      title,
      index: 0,
      items: () =>
        targets(this.v, kind)
          .sort((a, b) => cornerStrength(this.v, b) - cornerStrength(this.v, a))
          .map((c) => ({
            text: cornerSummary(this.v, c, this.model.prob),
            jump: c,
            action: () => {
              this.close(true);
              const action: KolonieAction =
                kind === "setupSettlement" ? { type: "placeSettlement", corner: c } : kind === "city" ? { type: "buildCity", corner: c } : { type: "buildSettlement", corner: c };
              this.withConfirm(`Postavit ${kind === "city" ? "město" : "základnu"}: ${cornerSummary(this.v, c, this.model.prob)}?`, () => this.send(action));
            },
          })),
    });
  }

  private openRoadList(kind: "setupRoad" | "freeRoad" | "road") {
    const v = this.v;
    if (kind === "road" && !afford(v, COSTS.tunel)) return say(`Chybí ti: ${bagText(missing(v, COSTS.tunel))}.`);
    const value = (e: number) => {
      const [a, b] = T.edges[e].corners;
      return Math.max(distanceOk(v, a) ? cornerStrength(v, a) : 0, distanceOk(v, b) ? cornerStrength(v, b) : 0);
    };
    this.open({
      title: "Kam postavit tunel",
      index: 0,
      items: () =>
        targets(this.v, kind)
          .sort((a, b) => value(b) - value(a))
          .map((e) => ({
            text: edgeSummary(this.v, e, this.model.prob),
            jump: T.edges[e].corners[0],
            action: () => {
              this.close(true);
              this.buildRoad(e, kind);
            },
          })),
    });
  }

  private openRobberList() {
    const v = this.v;
    const harm = (h: number) =>
      T.hexes[h].corners.reduce((n, c) => {
        const b = v.corners[c];
        if (!b || b.owner === v.me) return n;
        return n + (b.city ? 2 : 1) * (1 + v.players[b.owner].publicPoints / 3);
      }, 0) * (v.hexes[h].number ? 6 - Math.abs(7 - v.hexes[h].number!) : 0);
    const mineOnly = (h: number) => T.hexes[h].corners.some((c) => v.corners[c]?.owner === v.me);
    this.open({
      title: "Kam přesunout piráty",
      index: 0,
      items: () =>
        targets(this.v, "robber")
          .sort((a, b) => Number(mineOnly(a)) - Number(mineOnly(b)) || harm(b) - harm(a))
          .map((h) => {
            const owners = T.hexes[h].corners
              .map((c) => this.v.corners[c])
              .filter((b) => b !== null)
              .map((b) => `${b!.owner === this.v.me ? "ty" : this.v.players[b!.owner].name} ${b!.city ? "město" : "základna"}`);
            return {
              text: `${hexText(this.v.hexes[h], this.model.prob)}, pole ${T.hexes[h].label}; ${owners.length ? `stavby: ${owners.join(", ")}` : "bez staveb"}${mineOnly(h) ? "; je tu tvoje stavba" : ""}`,
              jump: T.hexes[h].corners[0],
              action: () => {
                this.close(true);
                this.send({ type: "moveRobber", hex: h });
              },
            };
          }),
    });
  }

  private openStealList() {
    this.open({
      title: "Komu ukradneš kartu",
      index: 0,
      items: () =>
        this.v.stealFrom.map((p) => ({
          text: `${this.v.players[p].name}, ${cards(this.v.players[p].cards)}`,
          action: () => {
            this.close(true);
            this.send({ type: "steal", victim: p });
          },
        })),
    });
  }

  private openDiscard() {
    this.discardDraft = {};
    const need = () => this.v.discardNeeded;
    this.open({
      title: `Odhoď ${cards(need())}`,
      index: 0,
      items: () => {
        const v = this.v;
        const chosen = bagTotal(this.discardDraft);
        const rows: Item[] = RESOURCES.filter((r) => v.hand![r] > 0).map((r) => ({
          text: `${resName(r)}: máš ${v.hand![r]}, odhodit ${this.discardDraft[r] ?? 0}`,
          adjust: (d) => {
            const n = (this.discardDraft[r] ?? 0) + d;
            if (n < 0 || n > v.hand![r] || (d > 0 && chosen >= need())) return play("okraj");
            this.discardDraft[r] = n;
          },
        }));
        rows.push({
          text: `Odhodit: vybráno ${chosen} z ${need()}`,
          action: () => {
            if (bagTotal(this.discardDraft) !== need()) return say(`Vyber přesně ${cards(need())}, teď máš ${bagTotal(this.discardDraft)}.`);
            this.close(true);
            this.send({ type: "discard", cards: this.discardDraft });
          },
        });
        return rows;
      },
    });
  }

  private openCards() {
    const v = this.v;
    const myTurn = myPending(v).some((p) => p.kind === "main" || p.kind === "roll");
    const playable = (d: { kind: Dev; fresh: boolean }) => myTurn && !d.fresh && d.kind !== "bod" && !v.devPlayedThisTurn;
    this.open({
      title: "Karty výzkumu",
      index: 0,
      items: () =>
        this.v.devHand.map((d) => ({
          text: `${DEV_NAME[d.kind]}: ${DEV_HELP[d.kind]}${playable(d) ? ", Enter zahraje" : d.fresh ? ", koupená v tomto tahu" : d.kind === "bod" ? "" : this.v.devPlayedThisTurn ? ", v tomto tahu už byla zahraná karta" : ""}`,
          action: () => {
            if (!playable(d)) return say("Tuhle kartu teď zahrát nemůžeš.");
            this.close(true);
            if (d.kind === "hlidka") return this.send({ type: "playKnight" });
            if (d.kind === "tunely") return this.send({ type: "playRoadBuilding" });
            if (d.kind === "monopol") return this.openResourcePick("Monopol: kterou surovinu ti všichni dají", (r) => this.send({ type: "playMonopoly", res: r }));
            this.yop = [];
            this.openResourcePick("Zásoby: vyber první surovinu", (r) => {
              this.yop.push(r);
              this.openResourcePick(`Druhá surovina (první: ${resName(r)})`, (r2) => this.send({ type: "playYearOfPlenty", res: [this.yop[0], r2] }));
            });
          },
        })),
    });
  }

  private openResourcePick(title: string, pick: (r: Res) => void) {
    this.open({
      title,
      index: 0,
      items: () =>
        RESOURCES.map((r) => ({
          text: `${resName(r)}${this.v.bank[r] ? "" : ", banka nemá"}`,
          action: () => {
            this.close(true);
            pick(r);
          },
        })),
    });
  }

  private openBank() {
    const v = this.v;
    if (!myPending(v).some((p) => p.kind === "main")) return say("S bankou můžeš měnit jen na svém tahu po hodu.");
    const gives = RESOURCES.filter((r) => v.hand![r] >= rateText(v, r));
    if (!gives.length) return say(`Na výměnu nemáš dost. ${this.ratesText()}`);
    this.open({
      title: "Banka: co dáš",
      index: 0,
      items: () =>
        RESOURCES.filter((r) => this.v.hand![r] >= rateText(this.v, r)).map((r) => ({
          text: `${resCount(rateText(this.v, r), r)}, máš ${this.v.hand![r]}`,
          action: () =>
            this.open({
              title: `Za ${resCount(rateText(this.v, r), r, true)} dostaneš`,
              index: 0,
              items: () =>
                RESOURCES.filter((g) => g !== r && this.v.bank[g] > 0).map((g) => ({
                  text: `1 ${resName(g)}`,
                  action: () => {
                    this.close(true);
                    this.send({ type: "bankTrade", give: r, get: g });
                  },
                })),
            }),
        })),
    });
  }

  private ratesText(): string {
    const v = this.v;
    const special = RESOURCES.filter((r) => rateText(v, r) < 4).map((r) => `${resName(r)} ${rateText(v, r)} za 1`);
    return special.length ? `Tvoje kurzy: ${special.join(", ")}, ostatní 4 za 1.` : "Měníš 4 za 1.";
  }

  /** Složení nabídky: šipka vpravo přidá do „chceš“, vlevo do „dáš“. */
  private openComposer(title: string, submit: (give: Bag, want: Bag) => void) {
    this.open({
      title: `${title}. Vpravo přidá do chceš, vlevo do dáš`,
      index: 0,
      items: () => {
        const v = this.v;
        const rows: Item[] = RESOURCES.map((r) => {
          const n = this.draft[r];
          const label = n < 0 ? `dáš ${-n}` : n > 0 ? `chceš ${n}` : "nic";
          return {
            text: `${resName(r)}: ${label}, máš ${v.hand![r]}`,
            adjust: (d) => {
              const next = this.draft[r] + d;
              if (next < 0 && -next > v.hand![r]) return play("okraj");
              this.draft[r] = next;
            },
          };
        });
        const give: Bag = {};
        const want: Bag = {};
        for (const r of RESOURCES) {
          if (this.draft[r] < 0) give[r] = -this.draft[r];
          if (this.draft[r] > 0) want[r] = this.draft[r];
        }
        rows.push({
          text: `Odeslat: dáš ${bagText(give, true)}, chceš ${bagText(want, true)}`,
          action: () => {
            if (!bagTotal(give) || !bagTotal(want)) return say("Nabídka musí něco dát a něco chtít.");
            this.close(true);
            submit(give, want);
          },
        });
        return rows;
      },
    });
  }

  private openTrade() {
    const v = this.v;
    const t = v.trade;
    if (t && t.from === v.me) return this.openResponses(t);
    if (t && t.from !== v.me) return say(`${this.offerText(t)} A přijmout, D odmítnout, P protinabídka.`);
    if (!myPending(v).some((p) => p.kind === "main")) return say("Obchod nabízí jen hráč na tahu po hodu.");
    this.draft = { kov: 0, sklo: 0, voda: 0, jidlo: 0, energie: 0 };
    this.openComposer("Nabídka ostatním", (give, want) => this.send({ type: "offerTrade", give, want, to: null }));
  }

  private openResponses(t: TradeOffer) {
    this.open({
      title: "Odpovědi na tvou nabídku",
      index: 0,
      items: () => {
        const v = this.v;
        const cur = v.trade;
        if (!cur) return [];
        const rows: Item[] = v.players
          .map((p, i) => ({ p, i }))
          .filter(({ i }) => i !== v.me && (cur.to === null || cur.to === i))
          .map(({ p, i }) => {
            const r = cur.responses[i];
            if (!r) return { text: `${p.name}: bez odpovědi` };
            if (r.kind === "decline") return { text: `${p.name}: odmítá` };
            const terms = r.kind === "counter" ? `protinabídka: dáš ${bagText(r.give, true)}, dostaneš ${bagText(r.want, true)}` : "přijímá";
            return { text: `${p.name}: ${terms}, Enter obchoduje`, action: () => (this.close(true), this.send({ type: "confirmTrade", offerId: cur.id, with: i })) };
          });
        rows.push({ text: "Zrušit nabídku", action: () => (this.close(true), this.send({ type: "cancelTrade" })) });
        return rows;
      },
    });
    void t;
  }

  private respond(response: "accept" | "decline") {
    const v = this.v;
    const t = v.trade;
    if (!t || !myPending(v).some((p) => p.kind === "tradeResponse")) return say("Žádná nabídka pro tebe.");
    if (response === "accept" && !afford(v, t.want)) return say(`Na to nemáš: chybí ${bagText(missing(v, t.want))}.`);
    this.send({ type: "respondTrade", offerId: t.id, response });
  }

  private openCounter() {
    const v = this.v;
    const t = v.trade;
    if (!t || !myPending(v).some((p) => p.kind === "tradeResponse")) return say("Žádná nabídka pro tebe.");
    // Výchozí protinabídka = obrácená nabídka (já dám, co on chce; chci, co on dává).
    this.draft = { kov: 0, sklo: 0, voda: 0, jidlo: 0, energie: 0 };
    for (const r of RESOURCES) this.draft[r] = (t.give[r] ?? 0) - (t.want[r] ?? 0);
    this.openComposer("Protinabídka", (give, want) => this.send({ type: "counterTrade", offerId: t.id, give: want, want: give }));
  }

  private openHistory() {
    this.open({ title: "Historie, nejnovější první", index: 0, items: () => this.model.log.map((text) => ({ text })) });
  }

  // ---------- Akce tahu ----------

  private roll() {
    if (!myPending(this.v).some((p) => p.kind === "roll")) return say(whatNow(this.v));
    this.send({ type: "roll" });
  }

  private endTurn() {
    const v = this.v;
    const kinds = myPending(v).map((p) => p.kind);
    if (kinds.includes("roadBuilding")) return this.withConfirm("Nestavět další tunel zdarma?", () => this.send({ type: "endTurn" }));
    if (!kinds.includes("main")) return say(whatNow(v));
    this.withConfirm("Ukončit tah?", () => this.send({ type: "endTurn" }));
  }

  rejected(reason: string) {
    play("okraj");
    const map: Record<string, string> = {
      "cannot-build": "Tohle teď postavit nejde.",
      "not-your-turn": "Teď nejsi na tahu.",
      "cannot-afford": "Na to nemáš suroviny.",
      "no-offer": "Ta nabídka už neplatí.",
    };
    say(map[reason] ?? "Tohle teď nejde.");
  }

  // ---------- Přehledy ----------

  statusText(): string {
    const v = this.v;
    if (v.phase === "over") return `Konec hry. ${this.scoreText()}`;
    const turn = v.turn === v.me ? "Jsi na tahu." : `Na tahu je ${v.players[v.turn].name}.`;
    const dice = v.dice ? ` Poslední hod ${v.dice[0] + v.dice[1]}.` : "";
    const robber = ` Piráti: ${hexText(v.hexes[v.robber], this.model.prob)}, pole ${T.hexes[v.robber].label}.`;
    const waiting = v.pending.filter((p) => p.player !== v.players[v.me!]?.id && p.kind !== "main");
    const wait = waiting.length ? ` Čeká se na: ${waiting.map((p) => v.players.find((pl) => pl.id === p.player)!.name).join(", ")}.` : "";
    return `${turn}${dice}${robber}${wait} ${whatNow(v)}`;
  }

  handText(): string {
    const v = this.v;
    if (!v.hand) return "Díváš se na hru.";
    const dev = v.devHand.length ? ` Karty výzkumu: ${v.devHand.map((d) => DEV_NAME[d.kind]).join(", ")}.` : "";
    const pc = v.players[v.me!].pieces;
    return `Máš ${bagText(v.hand)}.${dev} Zbývá ti ${pc.tunel} tunelů, ${pc.zakladna} základen, ${pc.mesto} měst. ${this.ratesText()}`;
  }

  scoreText(): string {
    const v = this.v;
    return v.players
      .map((p, i) => {
        const pts = i === v.me ? v.myPoints! : p.publicPoints;
        const extra = [v.longestHolder === i ? "nejdelší síť" : "", v.largestHolder === i ? "největší flotila" : ""].filter(Boolean).join(", ");
        return `${i === v.me ? "Ty" : p.name}: ${pointsText(pts)}, ${cards(p.cards)}${p.knights ? `, lodě ${p.knights}` : ""}${extra ? `, ${extra}` : ""}`;
      })
      .join(". ")
      .concat(`. Hraje se do ${v.targetPoints} bodů.`);
  }

  costsText(): string {
    const v = this.v;
    const line = (label: string, cost: Bag) => `${label}: ${bagText(cost)}${afford(v, cost) ? ", máš" : `, chybí ${bagText(missing(v, cost))}`}`;
    return [line("Tunel", COSTS.tunel), line("Základna", COSTS.zakladna), line("Město", COSTS.mesto), line("Výzkum", COSTS.vyzkum)].join(". ") + ".";
  }

  private offerText(t: TradeOffer): string {
    return `${this.v.players[t.from].name} nabízí: dá ${bagText(t.give, true)}, chce ${bagText(t.want, true)}.`;
  }

  // ---------- Události ----------

  /** Po aktualizaci pohledu: hlášení událostí a automatické otevření seznamu, když se na mě čeká. */
  onEvents(events: GameEvent[]) {
    const v = this.v;
    const me = v.me;
    const who = (p: number) => name(v, p);
    for (const e of events) {
      const p = e.player as number;
      switch (e.type) {
        case "started":
          this.tell(`Hra začala. ${v.players.length} hráči, hraje se do ${v.targetPoints} bodů. Nápověda klávesou H.`, 2);
          break;
        case "rolled": {
          queue("kolonie-kostky");
          const sum = e.sum as number;
          const text = p === me ? `Hodils ${sum}.` : `${who(p)}: padla ${sum}.`;
          this.tell(sum === 7 ? `${text} Sedmička: piráti.` : text, 2);
          if (sum === 7) queue("kolonie-pirati");
          break;
        }
        case "produced": {
          const gains = e.gains as Bag[];
          if (me !== null && bagTotal(gains[me])) {
            queue("kolonie-suroviny");
            this.tell(`Dostáváš ${bagText(gains[me])}.`, 2);
          }
          gains.forEach((g, i) => i !== me && bagTotal(g) && this.tell(`${who(i)} dostává ${bagText(g)}.`, 3));
          if ((e.blocked as Res[]).length) this.tell(`Banka nemá dost: ${(e.blocked as Res[]).map(resName).join(", ")} nedostane nikdo.`, 2);
          break;
        }
        case "startingResources":
          if (p === me) this.tell(`Startovní suroviny: ${bagText(e.gains as Bag)}.`, 2);
          break;
        case "discardNeeded": {
          const counts = e.counts as number[];
          if (me !== null && counts[me] > 0) {
            queue("kolonie-odhod");
            this.tell(`Máš moc karet: odhoď ${cards(counts[me])}.`, 1);
          }
          break;
        }
        case "discarded":
          this.tell(`${who(p)} odhazuje ${bagText(e.cards as Bag)}.`, p === me ? 2 : 3);
          break;
        case "robberMoved":
          queue("kolonie-pirati");
          this.tell(`Piráti${p === me ? "" : ` (${who(p)})`}: pole ${hexText(v.hexes[e.hex as number], this.model.prob)}, ${T.hexes[e.hex as number].label}.`, 2);
          break;
        case "stole":
          if (e.victim !== me && e.thief !== me) this.tell(`${who(e.thief as number)} krade kartu hráči ${who(e.victim as number)}.`, 3);
          if (e.nothing && e.thief === me) this.tell("Nebylo co ukrást.", 2);
          break;
        case "stoleCard":
          if (e.victim === me) {
            queue("kolonie-okraden");
            this.tell(`${who(e.thief as number)}: krádež, přicházíš o ${resCount(1, e.res as Res, true).replace("1 ", "")}.`, 1);
          } else if (e.thief === me) this.tell(`Ukradls ${resCount(1, e.res as Res, true).replace("1 ", "")}.`, 2);
          break;
        case "built": {
          const what = e.kind === "tunel" ? "tunel" : e.kind === "mesto" ? "město" : "základnu";
          const where = e.corner !== undefined ? `: ${cornerNeighbors(v, e.corner as number, this.model.prob)}` : e.edge !== undefined ? ` ${edgeSummaryShort(v, e.edge as number)}` : "";
          if (p === me) {
            queue("kolonie-stavba");
            this.tell(`Postaveno: ${what === "základnu" ? "základna" : what}.`, 2);
          } else {
            queue("kolonie-stavba", { gain: 0.5 });
            this.tell(`${who(p)} staví ${what}${where}.`, e.setup ? 2 : 3);
          }
          break;
        }
        case "devBought":
          if (p !== me) this.tell(`${who(p)} kupuje kartu výzkumu.`, 3);
          break;
        case "devCard":
          queue("kolonie-vyzkum");
          this.tell(`Koupils kartu: ${DEV_NAME[e.card as Dev]}.`, 2);
          break;
        case "devPlayed": {
          const card = e.card as Dev;
          if (p === me) break;
          if (card === "monopol") {
            const taken = (e.taken as number[])[me ?? -1] ?? 0;
            this.tell(`${who(p)}: monopol na ${resName(e.res as Res)}${taken ? `, bere ti ${resCount(taken, e.res as Res, true)}` : ""}.`, taken ? 1 : 2);
          } else this.tell(`${who(p)} hraje kartu: ${DEV_NAME[card]}.`, card === "hlidka" ? 2 : 3);
          break;
        }
        case "bankTraded":
          this.tell(`${p === me ? "Měníš" : `${who(p)} mění`} s bankou: ${resCount(e.rate as number, e.give as Res, true)} za 1 ${resName(e.get as Res)}.`, p === me ? 2 : 3);
          break;
        case "tradeOffered": {
          const t = e.offer as TradeOffer;
          if (t.from === me) {
            this.tell("Nabídka odeslána. Odpovědi uslyšíš, seznam klávesou O.", 2);
            break;
          }
          if (t.to !== null && t.to !== me) {
            this.tell(this.offerText(t), 3);
            break;
          }
          queue("kolonie-nabidka");
          if (afford(v, t.want)) this.tell(`${this.offerText(t)} A přijmout, D odmítnout, P protinabídka.`, 1);
          else this.tell(`${this.offerText(t)} Nemáš na to; D odmítne, P protinabídka.`, 2);
          break;
        }
        case "tradeResponse": {
          if (v.trade?.from !== me && !(e.player === me)) break;
          if (p === me) break;
          const r = e.response as string;
          const text = r === "accept" ? "přijímá" : r === "decline" ? "odmítá" : `protinabídka: dáš ${bagText(e.give as Bag, true)}, dostaneš ${bagText(e.want as Bag, true)}`;
          if (r !== "decline") queue("kolonie-nabidka");
          this.tell(`${who(p)} ${text}.${r !== "decline" ? " Klávesa O, Enter obchoduje." : ""}`, 2);
          break;
        }
        case "tradeDone": {
          const a = p;
          const b = e.with as number;
          if (a === me || b === me) {
            queue("kolonie-obchod");
            const give = (a === me ? e.give : e.want) as Bag;
            const get = (a === me ? e.want : e.give) as Bag;
            this.tell(`Obchod s hráčem ${who(a === me ? b : a)}: dáváš ${bagText(give, true)}, dostáváš ${bagText(get, true)}.`, 2);
          } else this.tell(`${who(a)} a ${who(b)} obchodují.`, 3);
          break;
        }
        case "tradeCancelled":
          if (v.me !== null && v.turn !== me) this.tell("Nabídka stažena.", 3);
          break;
        case "longestRoad":
          if (e.holder === null) this.tell("Nejdelší síť teď nemá nikdo.", 2);
          else {
            queue("kolonie-bonus");
            this.tell(`Nejdelší tunelová síť: ${who(e.holder as number)}, délka ${e.length}.`, 2);
          }
          break;
        case "largestArmy":
          queue("kolonie-bonus");
          this.tell(`Největší flotila: ${who(e.holder as number)}, ${e.knights} lodí.`, 2);
          break;
        case "turn": {
          if (p === me) {
            queue("tvuj-tah");
            const summary = this.model.since.length ? ` Od tvého tahu: ${this.model.since.slice(-5).join(" ")}${this.model.since.length > 5 ? " A další v historii, klávesa L." : ""}` : "";
            this.model.since = [];
            this.tell(e.setup ? "Jsi na řadě." : `Jsi na tahu.${summary}`, 1);
          } else {
            queue("souper-tah");
            this.tell(`Na tahu je ${who(p)}.`, e.setup ? 2 : 3);
          }
          break;
        }
        case "gameOver": {
          const win = e.winner as number;
          queue(win === me ? "vyhra" : "prohra");
          this.tell(`Konec hry. ${win === me ? "Vítěz: ty!" : `Vítěz: ${who(win)}.`} ${this.scoreText()}`, 1);
          break;
        }
      }
    }
    this.autoOpen();
  }

  /** Když se na mě čeká (rozestavění, odhazování, piráti, krádež), otevře rovnou správný seznam. */
  private autoOpen() {
    const v = this.v;
    const p = myPending(v).find((x) => x.kind !== "main" && x.kind !== "tradeResponse" && x.kind !== "roll");
    const key = p ? `${p.kind}:${v.turnCount}:${v.corners.filter(Boolean).length}:${v.edges.filter((e) => e !== null).length}` : "";
    if (!p || key === this.autoOpened) return;
    this.autoOpened = key;
    switch (p.kind) {
      case "setupSettlement":
        // Kurzor na nejlepší volné místo, aby šipky začínaly smysluplně.
        return this.openSettlementList("setupSettlement");
      case "setupRoad": {
        const c = setupCorner(v);
        if (c >= 0) this.model.cursor = c;
        return this.openRoadList("setupRoad");
      }
      case "discard":
        return this.openDiscard();
      case "robber":
        return this.openRobberList();
      case "steal":
        return this.openStealList();
      case "roadBuilding":
        return this.openRoadList("freeRoad");
    }
  }
}

/** Krátký popis hrany pro cizí stavbu: „mezi kovem na osmičce a vodou na šestce“. */
function edgeSummaryShort(v: KolonieView, e: number): string {
  const hexes = T.edges[e].hexes.map((h) => (v.hexes[h].terrain === "krater" ? "kráterem" : `${resName(v.hexes[h].terrain as Res)} ${v.hexes[h].number}`));
  return hexes.length === 2 ? `mezi poli ${hexes.join(" a ")}` : `u pole ${hexes[0]}`;
}
